import { Button } from "@shared/components/ui/Button";
import InfoTooltip from "@shared/components/InfoTooltip";
import { format } from "date-fns";
import React from "react";
import {
  Invitation,
  InvitationApprovalStatus,
  InvitationStatus,
} from "../types/invitation";
import { roleOptions } from "./InvitationForm";

interface InvitationsListProps {
  invitations: Invitation[];
  onDelete: (id: string) => void;
  onResend: (id: string) => void;
  onCopyLink?: (id: string) => void;
  onDeleteUser?: (id: string) => void;
  isLoading: boolean;
  title?: string;
  emptyMessage?: string;
  defaultRoleLabel?: string;
  /** Approval only applies to organization-to-member invitations. */
  showApproval?: boolean;
}

/**
 * Invitations created before GCCA review existed have no approval status, and
 * so do admin invitations: both are treated as already cleared.
 */
const approvalOf = (invitation: Invitation): InvitationApprovalStatus =>
  invitation.approvalStatus ?? InvitationApprovalStatus.APPROVED;

const isApproved = (invitation: Invitation) =>
  approvalOf(invitation) === InvitationApprovalStatus.APPROVED;

export const InvitationsList: React.FC<InvitationsListProps> = ({
  invitations,
  onDelete,
  onResend,
  onCopyLink,
  onDeleteUser,
  isLoading,
  title = "Sent Invitations",
  emptyMessage = "No invitations have been sent yet.",
  defaultRoleLabel = "Member",
  showApproval = false,
}) => {
  if (invitations.length === 0) {
    return (
      <div className="text-center py-8 bg-white shadow-md rounded-lg">
        <p className="text-gray-500">{emptyMessage}</p>
      </div>
    );
  }

  const formatDate = (date: Date | string) => {
    if (!date) return "N/A";
    const dateObj = typeof date === "string" ? new Date(date) : date;
    return format(dateObj, "MMM dd, yyyy");
  };

  const getStatusBadge = (status: InvitationStatus) => {
    switch (status) {
      case InvitationStatus.PENDING:
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
            Pending
          </span>
        );
      case InvitationStatus.ACCEPTED:
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
            Accepted
          </span>
        );
      case InvitationStatus.EXPIRED:
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
            Expired
          </span>
        );
      default:
        return null;
    }
  };

  const getApprovalBadge = (invitation: Invitation) => {
    switch (approvalOf(invitation)) {
      case InvitationApprovalStatus.PENDING:
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
            Awaiting GCCA
          </span>
        );
      case InvitationApprovalStatus.REJECTED:
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
            Rejected
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
            Approved
          </span>
        );
    }
  };

  const columnCount = showApproval ? 7 : 6;

  return (
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <h3 className="text-lg font-semibold p-6 border-b">{title}</h3>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              {showApproval && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  GCCA Approval
                </th>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sent Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Accepted Date
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody
            className="bg-white divide-y divide-gray-200"
            data-columns={columnCount}
          >
            {invitations.map((invitation) => {
              const awaitingApproval =
                approvalOf(invitation) === InvitationApprovalStatus.PENDING;
              const rejected =
                approvalOf(invitation) === InvitationApprovalStatus.REJECTED;

              return (
                <tr key={invitation.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {invitation.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {roleOptions.find(
                      (option) => option.value === invitation.role
                    )?.label || defaultRoleLabel}
                  </td>
                  {showApproval && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        {getApprovalBadge(invitation)}
                        {/* The detail lives one hover away: the column stays
                            scannable and the actions column keeps only actions. */}
                        {awaitingApproval && (
                          <InfoTooltip label="Why this invitation is waiting">
                            GCCA has to approve this before the invitation is
                            emailed. Until then it cannot be sent or shared.
                          </InfoTooltip>
                        )}
                        {rejected && (
                          <InfoTooltip label="Why this invitation was rejected">
                            GCCA did not approve this invitation, and nothing was
                            sent to this person. Contact a GCCA admin, or invite the
                            same address again.
                          </InfoTooltip>
                        )}
                      </div>
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(invitation.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(invitation.createdAt.toDate())}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {invitation.acceptedAt
                      ? formatDate(invitation.acceptedAt.toDate())
                      : "Not accepted"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {invitation.status === InvitationStatus.PENDING && (
                      <div className="flex flex-col items-end gap-1">
                        <div className="flex justify-end space-x-2">
                          {/* Copying the link would hand out a signup URL that
                              bypasses review, so it only appears once approved. */}
                          {onCopyLink && isApproved(invitation) && (
                            <Button
                              onClick={() => onCopyLink(invitation.id)}
                              variant="secondary"
                              size="sm"
                            >
                              Copy Link
                            </Button>
                          )}
                          {isApproved(invitation) && (
                            <Button
                              onClick={() => onResend(invitation.id)}
                              disabled={isLoading}
                              variant="link"
                              size="sm"
                            >
                              Resend
                            </Button>
                          )}
                          <Button
                            onClick={() => onDelete(invitation.id)}
                            disabled={isLoading}
                            variant="error"
                            size="sm"
                          >
                            {awaitingApproval ? "Cancel" : "Delete"}
                          </Button>
                        </div>
                      </div>
                    )}
                    {invitation.status === InvitationStatus.EXPIRED && (
                      <div className="flex justify-end space-x-2">
                        {isApproved(invitation) && (
                          <Button
                            onClick={() => onResend(invitation.id)}
                            disabled={isLoading}
                            variant="link"
                            size="sm"
                          >
                            Resend
                          </Button>
                        )}
                        <Button
                          onClick={() => onDelete(invitation.id)}
                          disabled={isLoading}
                          variant="error"
                          size="sm"
                        >
                          Delete
                        </Button>
                      </div>
                    )}
                    {invitation.status === InvitationStatus.ACCEPTED &&
                      onDeleteUser && (
                        <div className="flex justify-center space-x-2">
                          <Button
                            onClick={() => onDeleteUser(invitation.id)}
                            disabled={isLoading}
                            variant="error"
                            size="sm"
                          >
                            Delete User
                          </Button>
                        </div>
                      )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
